import {
  Box,
  Link,
  Stack,
  useTheme,
  Checkbox,
  MenuItem,
  Typography,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { TbShirtSport } from "react-icons/tb";
import { HiOutlineTicket } from "react-icons/hi";
import { useFormContext } from "react-hook-form";
import { OpenInNewRounded } from "@mui/icons-material";
import { MdOutlineEventSeat, MdOutlinedFlag } from "react-icons/md";

export const howItWorks = [
  {
    icon: <MdOutlinedFlag />,
    title: "Choose Your Games ",
    description: "Select the games you wish to attend.",
  },
  {
    icon: <TbShirtSport />,
    title: "Login to Account Manager",
    description: (
      <>
        Click Continue and connect to your{" "}
        <Link
          href={"https://am.ticketmaster.com/spectacor/"}
          target={"_blank"}
          sx={{
            color: "primary.main",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          Philadelphia Flyers
        </Link>{" "}
        account using our secure Ticketmaster login.
      </>
    ),
  },
  {
    icon: <MdOutlineEventSeat />,
    title: "Select Your Seats",
    description: "Select the seats you wish to purchase for the games.",
  },
  {
    icon: <HiOutlineTicket />,
    title: "Get Your Tickets",
    description: (
      <>
        Tickets will be available in your{" "}
        <Link
          href={"https://am.ticketmaster.com/spectacor/"}
          target={"_blank"}
          sx={{
            color: "primary.main",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          Philadelphia Flyers
        </Link>{" "}
        Account Manager within 24 hours of redemption. Reach out to Live Fan
        Support for any questions.
      </>
    ),
  },
];

export const faqs = [
  {
    title: "How do the Philadelphia Flyers Flex Plans work?",
    description:
      "The Philadelphia Flyers Flex Plan lets you fully customize your experience. You choose any home games that best fit your schedule and select any available seats for each game—giving you flexibility and control over your season.",
  },
  {
    title: "Do I have to select all my games now?",
    description: "Yes, you must select all games at the time of purchase.",
  },
  {
    title: "Can I choose seats throughout the venue?",
    description: "Yes, you can choose from all current available inventory.",
  },
  {
    title: "Where are my tickets delivered?",
    description: (
      <>
        Event tickets are delivered directly within your{" "}
        <Link
          href={"https://am.ticketmaster.com/spectacor/"}
          target={"_blank"}
          sx={{
            color: "primary.main",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          Philadelphia Flyers
        </Link>{" "}
        Account Manager.
      </>
    ),
  },
  {
    title: "What if I'm interested in a larger ticket package?",
    description: (
      <>
        The Philadelphia Flyers also offer Full and Half Season Ticket
        Memberships that provide enhanced benefits—including access to exclusive
        member events, discounts on food and merchandise, and playoff priority
        for your seats. Memberships also include a flexible ticket exchange
        program, giving you added convenience if your plans change during the
        season.
        <br />
        <br />
        To learn more, please visit:
        <ul>
          <li>
            Full/Half Season Ticket Memberships:{" "}
            <Link
              href="https://www.nhl.com/flyers/tickets/season-ticket-membership"
              target="_blank"
              sx={{
                color: "primary.main",
                textDecoration: "none",
                fontWeight: 500,
              }}
            >
              Season Ticket Membership
            </Link>
          </li>
          <li>
            12-Game Partial Plans:{" "}
            <Link
              href="https://www.nhl.com/flyers/tickets/partial-plans"
              target="_blank"
              sx={{
                color: "primary.main",
                textDecoration: "none",
                fontWeight: 500,
              }}
            >
              Partial Plans
            </Link>
          </li>
        </ul>
      </>
    ),
  },
];

export const PhiladelphiaRegistrationTerms = () => (
  <>
    By checking this box you agree to the{" "}
    <Link
      sx={{
        color: "primary.main",
        textDecoration: "none",
        fontWeight: 500,
      }}
      target={"_blank"}
      href="https://www.attend.tech/terms-of-use"
    >
      Terms of Use
    </Link>{" "}
    and{" "}
    <Link
      target={"_blank"}
      sx={{
        color: "primary.main",
        textDecoration: "none",
        fontWeight: 500,
      }}
      href="https://www.attend.tech/privacy-policy"
    >
      Privacy Policy
    </Link>
    .
  </>
);

export const PhiladelphiaCheckoutTerms = () => (
  <>
    By purchasing you agree to receive SMS alerts regarding ticket delivery and
    agree to the{" "}
    <Link
      target={"_blank"}
      sx={{
        color: "primary.main",
        textDecoration: "none",
        fontWeight: 500,
      }}
      href="https://www.attend.tech/terms-of-use"
    >
      Terms of Use
    </Link>{" "}
    and{" "}
    <Link
      target={"_blank"}
      sx={{
        color: "primary.main",
        textDecoration: "none",
        fontWeight: 500,
      }}
      href="https://www.attend.tech/privacy-policy"
    >
      Privacy Policy
    </Link>
    .
  </>
);

export const FlyersHamburgerList = () => (
  <Box>
    <MenuItem
      onClick={() => {
        window.open(
          "https://go.comcastspectacor.com/flexplanrequest",
          "_blank"
        );
      }}
    >
      <ListItemIcon>
        <OpenInNewRounded fontSize="small" />
      </ListItemIcon>
      <ListItemText>Have questions?</ListItemText>
    </MenuItem>
  </Box>
);

export const AdditionalRegistrationInputFields = () => {
  const theme = useTheme();
  const {
    register,
    formState: { errors },
  } = useFormContext();

  return (
    <Stack direction={"row"} alignItems={"flex-start"}>
      <Box>
        <Checkbox
          {...register("secondaryTerms", {
            required: "You need to accept the terms and conditions",
          })}
          sx={{ marginLeft: "-8px", marginTop: "-8px" }}
        />
      </Box>
      <Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Please share my name and email address with the Philadelphia Flyers
            so that they may email me marketing offers and other promotional
            information. I understand that I can unsubscribe from Philadelphia
            Flyers emails at any time by clicking the 'unsubscribe' link
            included with each such email.
          </Typography>
        </Box>
        <Box>
          {errors?.secondaryTerms?.message ? (
            <Typography
              variant="caption"
              color={theme.palette.error.main}
              lineHeight={1.1}
            >
              {errors?.secondaryTerms?.message as string}
            </Typography>
          ) : null}
        </Box>
      </Box>
    </Stack>
  );
};
